/**
 * React hooks for Time & Attendance features
 *
 * Provides TanStack Query hooks for time tracking operations
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as timeApi from '../lib/api/time.client';
/**
 * Query keys for time-related queries
 */
export const timeKeys = {
    all: ['time'],
    session: () => [...timeKeys.all, 'session'],
    punches: () => [...timeKeys.all, 'punches'],
    punchHistory: (startDate, endDate) => [...timeKeys.punches(), 'history', startDate, endDate],
    timesheets: () => [...timeKeys.all, 'timesheets'],
    timesheetSummary: (employeeId) => [...timeKeys.timesheets(), 'summary', employeeId],
    payPeriodConfig: () => [...timeKeys.all, 'payPeriodConfig'], // Legacy single config
    payPeriodConfigs: () => [...timeKeys.all, 'payPeriodConfigs'], // Multiple configs
    mobilePunchConfig: () => [...timeKeys.all, 'mobilePunchConfig'],
    corrections: () => [...timeKeys.all, 'corrections'],
    correction: (id) => [...timeKeys.corrections(), id],
};
/**
 * Hook to get current punch session
 *
 * Returns the employee's current clock status (clocked in/out, on break, hours worked)
 */
export function useCurrentSession() {
    return useQuery({
        queryKey: timeKeys.session(),
        queryFn: () => timeApi.getCurrentSession(),
        refetchInterval: 30000, // Refetch every 30 seconds to keep status fresh
        staleTime: 10000, // Consider data stale after 10 seconds
        retry: (failureCount, error) => {
            // Don't retry on 404 (no session exists yet)
            if (error instanceof Error && error.message.includes('404')) {
                return false;
            }
            return failureCount < 2; // Retry other errors up to 2 times
        },
    });
}
/**
 * Hook to create a punch (clock in/out/break)
 *
 * Includes optimistic updates for immediate UI feedback
 */
export function useCreatePunch() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request) => timeApi.createPunch(request),
        onMutate: async (request) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey: timeKeys.session() });
            // Snapshot current value
            const previousSession = queryClient.getQueryData(timeKeys.session());
            // Optimistically update session based on action
            queryClient.setQueryData(timeKeys.session(), (old) => {
                if (!old)
                    return old;
                switch (request.action) {
                    case 'CLOCK_IN':
                        return { ...old, isClockedIn: true, isOnBreak: false };
                    case 'CLOCK_OUT':
                        return { ...old, isClockedIn: false, isOnBreak: false };
                    case 'BREAK_START':
                        return { ...old, isOnBreak: true };
                    case 'BREAK_END':
                        return { ...old, isOnBreak: false };
                    default:
                        return old;
                }
            });
            return { previousSession };
        },
        onError: (_err, _request, context) => {
            // Rollback on error
            if (context?.previousSession) {
                queryClient.setQueryData(timeKeys.session(), context.previousSession);
            }
        },
        onSuccess: () => {
            // Invalidate queries to refetch with real data
            void queryClient.invalidateQueries({ queryKey: timeKeys.session() });
            void queryClient.invalidateQueries({ queryKey: timeKeys.punches() }); // Invalidates all punch-related queries
            void queryClient.invalidateQueries({ queryKey: timeKeys.all }); // Force refetch everything time-related
        },
    });
}
/**
 * Hook to get punch history
 *
 * Fetches list of punches with optional date range filtering
 */
export function usePunchHistory(providedStartDate, providedEndDate) {
    return useQuery({
        queryKey: timeKeys.punchHistory(providedStartDate, providedEndDate),
        queryFn: () => {
            const now = new Date().toISOString().split('T')[0];
            const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            return timeApi.getPunchHistory((providedStartDate || thirtyDaysAgo), (providedEndDate || now));
        },
        staleTime: 0, // Always refetch when component mounts or dependencies change
        refetchOnMount: 'always', // Always refetch when component mounts
        retry: (failureCount, error) => {
            // Don't retry on 404 (no punches exist yet)
            if (error instanceof Error && error.message.includes('404')) {
                return false;
            }
            return failureCount < 2; // Retry other errors up to 2 times
        },
    });
}
/**
 * Hook to get combined punch clock state
 *
 * Convenience hook that provides both session and recent history
 */
export function usePunchClock() {
    const session = useCurrentSession();
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // Always include tomorrow to handle UTC timezone issues
    const history = usePunchHistory(sevenDaysAgo, tomorrow); // Last 7 days + tomorrow
    const createPunch = useCreatePunch();
    return {
        // Session state
        session: session.data,
        isLoading: session.isLoading,
        error: session.error,
        // History
        history: history.data?.punches || [],
        historyLoading: history.isLoading,
        // Actions
        clockIn: (data) => createPunch.mutate({ ...data, action: 'CLOCK_IN' }),
        clockOut: (data) => createPunch.mutate({ ...data, action: 'CLOCK_OUT' }),
        startBreak: (data) => createPunch.mutate({ ...data, action: 'BREAK_START' }),
        endBreak: (data) => createPunch.mutate({ ...data, action: 'BREAK_END' }),
        // Mutation state
        isPunching: createPunch.isPending,
        punchError: createPunch.error,
    };
}
// ============================================================================
// Timesheet Hooks
// ============================================================================
/**
 * Hook to get timesheet summaries (current and previous pay periods)
 */
export function useTimesheets(employeeId) {
    return useQuery({
        queryKey: timeKeys.timesheetSummary(employeeId),
        queryFn: () => timeApi.getTimesheets(employeeId),
        staleTime: 60000, // 1 minute - timesheets don't change frequently
        refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    });
}
// ============================================================================
// Mobile Punch Configuration Hooks (Org-level setting)
// ============================================================================
/**
 * Hook to read the organization's mobile punch setting
 */
export function useMobilePunchConfig() {
    return useQuery({
        queryKey: timeKeys.mobilePunchConfig(),
        queryFn: () => timeApi.getMobilePunchConfig(),
        staleTime: 5 * 60 * 1000, // 5 minutes - rarely changes
    });
}
/**
 * Hook to update the organization's mobile punch setting (admin only)
 */
export function useUpdateMobilePunchConfig() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (enabled) => timeApi.updateMobilePunchConfig(enabled),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: timeKeys.mobilePunchConfig() });
        },
    });
}
// ============================================================================
// Pay Period Configuration Hooks (Admin Only)
// ============================================================================
/**
 * Hook to list all pay period configurations
 */
export function usePayPeriodConfigs() {
    return useQuery({
        queryKey: timeKeys.payPeriodConfigs(),
        queryFn: () => timeApi.listPayPeriodConfigs(),
        staleTime: 5 * 60 * 1000, // 5 minutes - configs don't change often
    });
}
/**
 * Hook to create a new pay period configuration
 */
export function useCreatePayPeriodConfig() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: timeApi.createPayPeriodConfig,
        onSuccess: () => {
            // Invalidate configs list
            void queryClient.invalidateQueries({ queryKey: timeKeys.payPeriodConfigs() });
            // Also invalidate timesheet queries since pay periods may have changed
            void queryClient.invalidateQueries({ queryKey: timeKeys.timesheets() });
        },
    });
}
/**
 * Hook to delete a pay period configuration
 */
export function useDeletePayPeriodConfig() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: timeApi.deletePayPeriodConfig,
        onSuccess: () => {
            // Invalidate configs list
            void queryClient.invalidateQueries({ queryKey: timeKeys.payPeriodConfigs() });
        },
    });
}
/**
 * LEGACY: Hook to get pay period configuration (deprecated - use usePayPeriodConfigs)
 */
export function usePayPeriodConfig() {
    return useQuery({
        queryKey: timeKeys.payPeriodConfig(),
        queryFn: () => timeApi.getPayPeriodConfig(),
        staleTime: 5 * 60 * 1000, // 5 minutes - config doesn't change often
    });
}
/**
 * LEGACY: Hook to update pay period configuration (deprecated)
 */
export function useUpdatePayPeriodConfig() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: timeApi.updatePayPeriodConfig,
        onSuccess: () => {
            // Invalidate pay period config query
            void queryClient.invalidateQueries({ queryKey: timeKeys.payPeriodConfig() });
            // Also invalidate timesheet queries since pay periods changed
            void queryClient.invalidateQueries({ queryKey: timeKeys.timesheets() });
        },
    });
}
/**
 * Hook to get pending timesheet approvals (manager-only)
 *
 * Returns list of timesheets awaiting manager approval from direct reports
 */
export function useListTimesheetApprovals() {
    return useQuery({
        queryKey: [...timeKeys.timesheets(), 'approvals'],
        queryFn: () => timeApi.listTimesheetApprovals(),
        staleTime: 1 * 60 * 1000, // 1 minute - approvals should be relatively fresh
    });
}
/**
 * Hook to approve or reject a timesheet (manager-only)
 */
export function useApproveTimesheet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: timeApi.approveTimesheet,
        onSuccess: () => {
            // Invalidate timesheet approvals and summaries
            void queryClient.invalidateQueries({ queryKey: [...timeKeys.timesheets(), 'approvals'] });
            void queryClient.invalidateQueries({ queryKey: timeKeys.timesheets() });
        },
    });
}
/**
 * Hook to list correction requests for manager approval
 * @param view - 'my' for employee's own corrections, 'manager' for pending approvals
 * @param status - Filter by correction status (pending, approved, rejected)
 */
export function useCorrections(view, status) {
    return useQuery({
        queryKey: [...timeKeys.corrections(), view, status],
        queryFn: () => timeApi.listCorrections(view, status),
        staleTime: 1 * 60 * 1000, // 1 minute
    });
}
/**
 * Hook to get a single correction request by ID
 */
export function useCorrection(correctionId) {
    return useQuery({
        queryKey: timeKeys.correction(correctionId),
        queryFn: () => timeApi.getCorrection(correctionId),
        enabled: !!correctionId,
    });
}
/**
 * Hook to approve or reject a correction request (manager-only)
 */
export function useReviewCorrection() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: timeApi.reviewCorrection,
        onSuccess: () => {
            // Invalidate corrections list
            void queryClient.invalidateQueries({ queryKey: timeKeys.corrections() });
        },
    });
}
